<template>
  <b-modal
    ref="modalConferencia"
    id="modal-conferencia-vendas"
    size="xl"
    header-bg-variant="dark"
    header-text-variant="white"
    no-close-on-backdrop
    body-class="p-0"
    @shown="onModalShown"
    @hidden="onModalHidden"
  >
    <template #modal-header="{ close }">
      <div class="d-flex justify-content-between align-items-center w-100">
        <div class="d-flex align-items-center">
          <div class="modal-icon-header mr-3">
            <i class="fa fa-barcode"></i>
          </div>
          <div>
            <h5 class="mb-0 text-white font-weight-bold">Separacao de Vendas</h5>
            <small class="text-light opacity-75">Pedido #{{ pedidoId }} - {{ clienteNome }}</small>
          </div>
        </div>
        <b-button size="sm" variant="link" class="text-white" @click="close()">
          <i class="fa fa-times fa-lg"></i>
        </b-button>
      </div>
    </template>

    <div class="modal-body-premium">
      <div :class="['banner-status', `banner-status--${bannerStatus.variant}`]">
        {{ bannerStatus.text }}
      </div>

      <b-row no-gutters>
        <b-col lg="8" class="border-right">
          <div class="p-4 h-100 bg-light">
            <div class="scan-section mb-4 shadow-sm">
              <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                <label class="scan-label mb-0">Aguardando leitura do codigo de barras...</label>
                <div class="d-flex align-items-center ml-auto" style="gap: 0.5rem;">
                  <span class="font-weight-bold text-secondary" style="font-size:0.95rem;">Modo:</span>
                  <b-form-group class="mb-0" label="" label-for="modo-coleta-switch">
                    <b-form-radio-group
                      id="modo-coleta-switch"
                      v-model="modoColeta"
                      :options="[
                        { text: 'Adicionar', value: 'adicionar' },
                        { text: 'Remover', value: 'remover' }
                      ]"
                      buttons
                      button-variant="outline-primary"
                      size="sm"
                      class="modo-coleta-switch"
                    />
                  </b-form-group>
                </div>
              </div>
              <div class="scan-input-wrap">
                <i class="fa fa-barcode scan-icon"></i>
                <input
                  ref="barcodeInput"
                  v-model="barcode"
                  class="scan-input"
                  placeholder="Escaneie a etiqueta aqui..."
                  @keyup.enter="registrarConferencia"
                  :disabled="loading"
                  autocomplete="off"
                />
                <b-spinner v-if="loadingConferencia" small variant="primary" class="scan-spinner" />
              </div>
              <div v-if="ultimaEtiqueta" class="last-scan-feedback mt-2">
                <i class="fa fa-check-circle text-success mr-2"></i>
                <span>Ultimo: <strong>{{ ultimaEtiqueta.DESCRICAO || ultimaEtiqueta.PRODUTO_ID }}</strong> - {{ formatarQuant(ultimaEtiqueta.PESO) }}kg</span>
              </div>
            </div>

            <div class="summary-section bg-white rounded-lg p-3 shadow-xs border">
              <h6 class="section-title mb-3">
                <i class="fa fa-list-alt mr-2 text-primary"></i>Resumo da Separacao
              </h6>
              <b-table
                :items="itens"
                :fields="camposResumo"
                small
                striped
                hover
                borderless
                class="mb-0 table-modern"
              >
                <template #cell(DESCRICAO)="{ item }">
                  <div class="text-truncate">
                    <span class="font-weight-bold text-primary">{{ item.COD_PRO }}</span> - {{ item.DESCRICAO }}
                  </div>
                </template>

                <template #cell(PROGRESSO)="{ item }">
                  <div class="d-flex align-items-center gap-2">
                    <b-progress
                      :value="Number(item.PECAS_CONFERIDA)"
                      :max="Number(item.PECAS_PEDIDO)"
                      height="8px"
                      class="flex-grow-1 rounded-pill"
                      :variant="calcPct(item) >= 100 ? 'success' : 'primary'"
                    />
                    <span class="font-xs font-weight-bold text-muted text-nowrap">
                      {{ Math.floor(item.PECAS_CONFERIDA) }} / {{ Math.floor(item.PECAS_PEDIDO) }}
                      <span class="mx-1">|</span>
                      <span class="text-dark">{{ formatarQuant(item.QUANT_CONFERIDA) }}</span> {{ item.UNIDADE || 'KG' }}
                    </span>
                  </div>
                </template>
              </b-table>
            </div>
          </div>
        </b-col>

        <b-col lg="4">
          <div class="p-0 d-flex flex-column h-100 bg-white" style="max-height: 65vh;">
            <div class="p-3 bg-dark text-white d-flex justify-content-between align-items-center">
              <h6 class="mb-0 font-weight-bold"><i class="fa fa-history mr-2 text-warning"></i>Ultimas Leituras</h6>
              <b-badge variant="warning" pill>{{ etiquetas.length }}</b-badge>
            </div>

            <div class="flex-grow-1 overflow-auto p-2">
              <div v-if="etiquetas.length === 0" class="text-center p-5 text-muted">
                <i class="fa fa-inbox fa-3x mb-3 opacity-25"></i>
                <p>Nenhuma etiqueta separada.</p>
              </div>
              <div v-else>
                <div v-for="(etiqueta, idx) in etiquetasOrdenadas" :key="etiqueta.ID" class="log-item shadow-xs">
                  <div class="log-item__left">
                    <div class="log-item__seq">#{{ etiquetas.length - idx }}</div>
                  </div>
                  <div class="log-item__info overflow-hidden">
                    <div class="log-item__prod text-truncate">{{ etiqueta.DESCRICAO || etiqueta.PRODUTO_ID }}</div>
                    <div class="log-item__meta text-truncate">
                      <span><i class="fa fa-tag mr-1"></i>{{ etiqueta.CODIGO_BARRAS }}</span>
                    </div>
                  </div>
                  <div class="log-item__right d-flex flex-column align-items-end">
                    <div class="log-item__weight">{{ formatarQuant(etiqueta.PESO) }}</div>
                    <b-button size="sm" variant="link" class="text-danger p-0" @click="removerConferencia(etiqueta)">
                      <i class="fa fa-trash"></i>
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <template #modal-footer>
      <div class="d-flex justify-content-between w-100 align-items-center">
        <div class="d-flex align-items-center">
          <div class="footer-totalizer shadow-sm">
            <span class="totalizer-count">{{ Math.floor(totalPecasConferidas) }}/{{ Math.floor(totalPecasPedido) }}</span>
            <span class="totalizer-divider mx-3">|</span>
            <span class="totalizer-weight">{{ formatarQuant(totalPesoConferido) }}</span>
            <span class="totalizer-unit ml-1">kg</span>
          </div>
        </div>
        <div class="d-flex gap-2">
          <b-button variant="outline-secondary" class="px-4" @click="fechar">SAIR</b-button>
          <b-button variant="success" class="px-4 font-weight-bold btn-finalizar" @click="finalizar" :disabled="loading">
            <i class="fa fa-check-circle mr-1"></i> FINALIZAR SEPARACAO
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import Tools from '@/tools/tools.js'

export default {
  name: 'ConferenciaVendasModal',
  props: {
    pedidoId: { type: [Number, String], default: null }
  },
  data () {
    return {
      loading: false,
      loadingConferencia: false,
      barcode: '',
      clienteNome: '',
      itens: [],
      etiquetas: [],
      ultimaEtiqueta: null,
      modoColeta: 'adicionar', // 'adicionar' ou 'remover'
      bannerStatus: {
        text: 'AGUARDANDO LEITURA',
        variant: 'warning'
      },
      camposResumo: [
        { key: 'DESCRICAO', label: 'Produto' },
        { key: 'PROGRESSO', label: 'Progresso', thClass: 'text-center' }
      ]
    }
  },
  computed: {
    etiquetasOrdenadas () {
      return [...this.etiquetas].reverse()
    },
    totalPecasPedido () {
      return this.itens.reduce((acc, item) => acc + (Number(item.PECAS_PEDIDO) || 0), 0)
    },
    totalPecasConferidas () {
      return this.itens.reduce((acc, item) => acc + (Number(item.PECAS_CONFERIDA) || 0), 0)
    },
    totalPesoConferido () {
      return this.itens.reduce((acc, item) => acc + (Number(item.QUANT_CONFERIDA) || 0), 0)
    }
  },
  methods: {
    abrir () {
      this.$bvModal.show('modal-conferencia-vendas')
      this.carregarDados()
    },
    fechar () {
      this.$bvModal.hide('modal-conferencia-vendas')
    },
    onModalShown () {
      this.focarInput()
    },
    onModalHidden () {
      this.$emit('closed')
    },
    focarInput () {
      this.$nextTick(() => {
        if (this.$refs.barcodeInput) this.$refs.barcodeInput.focus()
      })
    },
    async carregarDados () {
      if (!this.pedidoId) return
      this.loading = true
      try {
        const response = await this.$http.get(`/api/protected/separacao-vendas/itens-pedido/${this.pedidoId}`, { noToast: true })
        this.itens = response.data.itens || []
        this.etiquetas = response.data.etiquetas || []
        if (response.data.venda) {
          this.clienteNome = response.data.venda.NOME_CLIENTE
        }
      } catch (err) {
        console.error('Erro ao carregar conferencia:', err)
      } finally {
        this.loading = false
      }
    },
    async registrarConferencia () {
      if (!this.barcode || this.loadingConferencia) return

      const codigoBarras = this.barcode.trim()
      this.barcode = ''
      this.loadingConferencia = true

      if (this.modoColeta === 'adicionar') {
        try {
          const response = await this.$http.post('/api/protected/separacao-vendas/registrar', {
            pedidoId: this.pedidoId,
            codigoBarras,
            usuario: window.localStorage.getItem('login')
          }, { noToast: true })

          if (response.data.success) {
            this.ultimaEtiqueta = response.data.etiqueta
            this.bannerStatus = {
              text: `${this.ultimaEtiqueta.DESCRICAO || this.ultimaEtiqueta.PRODUTO_ID}: ${this.formatarQuant(this.ultimaEtiqueta.PESO)} KG`,
              variant: 'success'
            }
            this.tocarSomSucesso()
            await this.carregarDados()
          }
        } catch (err) {
          const msg = err.response?.data || (typeof err === 'string' ? err : err.message) || 'Erro ao registrar'
          this.bannerStatus = {
            text: String(msg).toUpperCase(),
            variant: 'danger'
          }
          this.tocarSomErro()
        } finally {
          this.loadingConferencia = false
          this.focarInput()
        }
      } else if (this.modoColeta === 'remover') {
        // Busca etiqueta pelo código de barras e executa a mesma lógica do botão remover
        const etiqueta = this.etiquetas.find(e => String(e.CODIGO_BARRAS) === codigoBarras)
        if (!etiqueta) {
          this.bannerStatus = {
            text: 'ETIQUETA NAO ENCONTRADA',
            variant: 'danger'
          }
          this.tocarSomErro()
          this.loadingConferencia = false
          this.focarInput()
          return
        }
        try {
          await this.$http.post('/api/protected/separacao-vendas/remover', {
            etiquetaMovimentoId: etiqueta.ETIQUETA_MOVIMENTO_ID
          }, { noToast: true })
          await this.carregarDados()
          this.bannerStatus = {
            text: `${etiqueta.DESCRICAO || etiqueta.PRODUTO_ID}: ${this.formatarQuant(etiqueta.PESO)} KG REMOVIDO`,
            variant: 'warning'
          }
          this.tocarSomSucesso()
        } catch (err) {
          Tools.exibirMensagem(this.$store, 'Erro', 'danger', 'Erro ao remover separacao')
          this.bannerStatus = { text: 'ERRO AO REMOVER', variant: 'danger' }
          this.tocarSomErro()
        } finally {
          this.loadingConferencia = false
          this.focarInput()
        }
      }
    },
    async removerConferencia (etiqueta) {
      const confirm = await this.$bvModal.msgBoxConfirm('Deseja estornar esta etiqueta da separacao?', {
        title: 'Estornar Separacao',
        okTitle: 'SIM',
        cancelTitle: 'NAO',
        centered: true
      })
      if (!confirm) return

      try {
        await this.$http.post('/api/protected/separacao-vendas/remover', {
          etiquetaMovimentoId: etiqueta.ETIQUETA_MOVIMENTO_ID
        }, { noToast: true })
        await this.carregarDados()
        this.bannerStatus = { text: 'SEPARACAO REMOVIDA', variant: 'warning' }
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', 'Erro ao remover separacao')
      }
    },
    async finalizar () {
      const totalP = this.itens.reduce((s, i) => s + Number(i.PECAS_PEDIDO), 0)
      const totalC = this.itens.reduce((s, i) => s + Number(i.PECAS_CONFERIDA), 0)

      let msg = 'Deseja finalizar a separacao deste pedido?'
      if (totalC < totalP) {
        msg = `O total de pecas separadas (${totalC}) e menor que o pedido (${totalP}). Deseja finalizar mesmo assim?`
      }

      const confirm = await this.$bvModal.msgBoxConfirm(msg, {
        title: 'Finalizar Separacao',
        okTitle: 'SIM, FINALIZAR',
        cancelTitle: 'CANCELAR',
        okVariant: 'success',
        centered: true
      })

      if (!confirm) return

      this.loading = true
      try {
        await this.$http.post('/api/protected/separacao-vendas/finalizar', {
          pedidoId: this.pedidoId,
          usuario: window.localStorage.getItem('login')
        })
        Tools.exibirMensagem(this.$store, 'Concluido', 'success', 'Separacao finalizada com sucesso!')
        this.$emit('concluido')
        this.fechar()
      } catch (err) {
        Tools.exibirMensagem(this.$store, 'Erro', 'danger', err)
      } finally {
        this.loading = false
      }
    },
    calcPct (item) {
      const p = Number(item.PECAS_PEDIDO) || 0
      const c = Number(item.PECAS_CONFERIDA) || 0
      if (p === 0) return 0
      return (c / p) * 100
    },
    formatarQuant (v) {
      return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    },
    tocarSomSucesso () {
      const audio = new Audio('/static/media/sucesso.wav')
      audio.play().catch(() => {})
    },
    tocarSomErro () {
      const audio = new Audio('/static/media/error.wav')
      audio.play().catch(() => {})
    }
  }
}
</script>

<style scoped>
.modo-coleta-switch .btn {
  min-width: 90px;
  font-weight: 600;
}
</style>

<style scoped>
.modal-body-premium { min-height: 550px; background: #f8fafc; }
.modal-icon-header {
  width: 42px;
  height: 42px;
  background: rgba(255,255,255,0.1);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.banner-status {
  padding: 16px 24px;
  font-size: 2rem;
  font-weight: 800;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(0,0,0,0.1);
}
.banner-status--warning { background-color: #f59e0b; }
.banner-status--success { background-color: #10b981; }
.banner-status--danger { background-color: #ef4444; }
.scan-section {
  background: #fff;
  padding: 24px;
  border-radius: 16px;
  border: 2px solid #eef2ff;
}
.scan-label { font-size: 0.85rem; font-weight: 600; color: #64748b; margin-bottom: 12px; display: block; }
.scan-input-wrap { position: relative; }
.scan-icon { position: absolute; left: 20px; top: 50%; transform: translateY(-50%); font-size: 24px; color: #6366f1; }
.scan-input {
  width: 100%;
  height: 64px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  padding: 0 60px 0 60px;
  font-size: 1.6rem;
  font-weight: 700;
  color: #1e293b;
  transition: all 0.2s;
}
.scan-input:focus {
  background: #fff;
  border-color: #6366f1;
  box-shadow: 0 0 0 5px rgba(99, 102, 241, 0.1);
  outline: none;
}
.scan-spinner { position: absolute; right: 20px; top: 50%; transform: translateY(-50%); }
.last-scan-feedback { font-size: 0.9rem; font-weight: 500; color: #475569; }
.section-title { font-size: 0.8rem; font-weight: 700; color: #1e293b; text-transform: uppercase; letter-spacing: 0.05em; }
.table-modern { font-size: 0.875rem; }
.table-modern th { border: none !important; color: #64748b; font-weight: 600; text-transform: uppercase; font-size: 0.75rem; }
.log-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 8px;
  border: 1px solid #f1f5f9;
  transition: all 0.2s;
}
.log-item:hover { transform: translateX(-4px); background: #fff; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
.log-item__seq {
  width: 36px;
  height: 36px;
  background: #1e293b;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-weight: 800;
  font-size: 0.85rem;
}
.log-item__info { flex-grow: 1; }
.log-item__prod { font-weight: 700; color: #1e293b; font-size: 0.85rem; }
.log-item__meta { font-size: 0.75rem; color: #64748b; }
.log-item__right { flex-shrink: 0; text-align: right; min-width: 80px; }
.log-item__weight { font-size: 1.4rem; font-weight: 800; color: #4f46e5; line-height: 1; }
.text-truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.font-xs { font-size: 0.75rem; }
.gap-2 { gap: 8px; }
.opacity-75 { opacity: 0.75; }
.footer-totalizer {
  background: #1e293b;
  color: #fff;
  padding: 10px 24px;
  border-radius: 12px;
  display: flex;
  align-items: baseline;
  font-family: 'Inter', sans-serif;
  border: 1px solid rgba(255,255,255,0.1);
}
.totalizer-count { font-size: 1.8rem; font-weight: 800; }
.totalizer-divider { font-size: 1.5rem; opacity: 0.3; font-weight: 300; }
.totalizer-weight { font-size: 1.8rem; font-weight: 800; color: #fbbf24; }
.totalizer-unit { font-size: 0.9rem; font-weight: 700; text-transform: uppercase; color: #94a3b8; }
.btn-finalizar { height: 52px; font-size: 1rem; letter-spacing: 0.05em; box-shadow: 0 10px 15px -3px rgba(16, 185, 129, 0.2); }
</style>



// WEBPACK FOOTER //
// src/views/expedicao/conferencia/ConferenciaVendasModal.vue